import type { AuthedAxiosRequests } from 'features/auth'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer'
import { transformSystemDocumentToTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/helpers'
import { SystemDocumentEntrySchema } from 'features/knowledgeDocumentViewer/types/systemDocumentEntrySchemas'
import { SystemDocumentPageEntrySchema } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'

import type { LotoAttachment, LotoReportScope } from './lotoReportSchemas'
import { loadAllRows } from './lotoRolodexLoad'

type EntryTags = Record<string, string | null> | undefined

export type LotoPageTarget = { documentId: string; pageNumber: number }

export type LotoPageDetails = {
  pageIdToTarget: ReadonlyMap<string, LotoPageTarget>
  systemDocumentIdToTitle: ReadonlyMap<string, string>
  systemDocumentIdToNumber: ReadonlyMap<string, string>
  systemDocumentIdToDocument: ReadonlyMap<string, DocumentTableRow>
}

function isInSite(tags: EntryTags, scope: LotoReportScope): boolean {
  return tags?.organization === scope.organization && tags?.site === scope.site
}

async function loadSiteEntriesById(
  axios: AuthedAxiosRequests,
  endpoint: string,
  scope: LotoReportScope,
  type: string,
  ids: string[]
): Promise<unknown[]> {
  if (!ids.length) return []
  return loadAllRows(axios, endpoint, {
    type: [type],
    tags: { organization: scope.organization, site: scope.site },
    ids,
  })
}

function createPageIdToTarget(
  row: unknown,
  scope: LotoReportScope,
  wanted: ReadonlySet<string>
): [string, LotoPageTarget] | undefined {
  const parsed = SystemDocumentPageEntrySchema.safeParse(row)
  if (!parsed.success) return undefined
  const { id, data, tags } = parsed.data.entry
  const pageNumber = data.page_num
  if (
    !wanted.has(id) ||
    !isInSite(tags, scope) ||
    !data.document_id ||
    pageNumber === undefined ||
    !Number.isInteger(pageNumber) ||
    pageNumber < 1
  )
    return undefined
  return [id, { documentId: data.document_id, pageNumber }]
}

function createSystemDocumentIdToTitle(
  row: unknown,
  scope: LotoReportScope,
  wanted: ReadonlySet<string>
): [string, string] | undefined {
  const parsed = SystemDocumentEntrySchema.safeParse(row)
  if (!parsed.success) return undefined
  const { id, data, tags } = parsed.data.entry
  const title = data.document_title?.trim() || data.title.trim()
  if (!wanted.has(id) || !isInSite(tags, scope) || !title) return undefined
  return [id, title]
}

function createSystemDocumentIdToNumber(
  row: unknown,
  scope: LotoReportScope,
  wanted: ReadonlySet<string>
): [string, string] | undefined {
  const parsed = SystemDocumentEntrySchema.safeParse(row)
  if (!parsed.success) return undefined
  const { id, data, tags } = parsed.data.entry
  const number = data.drawing_number?.trim()
  if (!wanted.has(id) || !isInSite(tags, scope) || !number) return undefined
  return [id, number]
}

function createSystemDocumentIdToDocument(
  row: unknown,
  scope: LotoReportScope,
  wanted: ReadonlySet<string>
): [string, DocumentTableRow] | undefined {
  const parsed = SystemDocumentEntrySchema.safeParse(row)
  if (!parsed.success) return undefined
  const { id, tags } = parsed.data.entry
  if (!wanted.has(id) || !isInSite(tags, scope)) return undefined
  return [id, transformSystemDocumentToTableRow(parsed.data)]
}

function collect<T>(
  rows: unknown[],
  pick: (row: unknown) => [string, T] | undefined
): Map<string, T> {
  return new Map(
    rows.flatMap((row) => {
      const picked = pick(row)
      return picked ? [picked] : []
    })
  )
}

export async function loadLotoPageDetails(
  axios: AuthedAxiosRequests,
  endpoint: string,
  scope: LotoReportScope,
  attachments: LotoAttachment[]
): Promise<LotoPageDetails> {
  const pageIds = new Set(attachments.flatMap(({ page_ids }) => page_ids))
  const documentIds = new Set(
    attachments.map(({ system_document_id }) => system_document_id)
  )
  const [pageRows, documentRows] = await Promise.all([
    loadSiteEntriesById(axios, endpoint, scope, 'system-document-page', [
      ...pageIds,
    ]),
    loadSiteEntriesById(axios, endpoint, scope, 'system-document', [
      ...documentIds,
    ]),
  ])
  return {
    pageIdToTarget: collect(pageRows, (row) =>
      createPageIdToTarget(row, scope, pageIds)
    ),
    systemDocumentIdToTitle: collect(documentRows, (row) =>
      createSystemDocumentIdToTitle(row, scope, documentIds)
    ),
    systemDocumentIdToNumber: collect(documentRows, (row) =>
      createSystemDocumentIdToNumber(row, scope, documentIds)
    ),
    systemDocumentIdToDocument: collect(documentRows, (row) =>
      createSystemDocumentIdToDocument(row, scope, documentIds)
    ),
  }
}
